import { Controller } from "@hotwired/stimulus";

import { get } from "@rails/request.js";

const FIELD_CONFIG = {
  dateFields: [
    "app_in_date",
    "approved_date",
    "created_at",
    "deployment_date",
    "e_sign_date",
    "activation_call_date",
    "boarding_in_progress_date",
    "uw_date",
    "resubmit_date",
    "declined_date",
    "in_progress_date",
    "shipped_date",
    "delivered_date",
    "installation_call_date",
    "processing_start_date",
    "invoices_invoice_date",
    "invoices_paid_date",
    "lease_funding_date",
    "agent_split_status",
    "funding_date",
    "agent_paid_date",
    "sm_paid_date",
    "dsm_paid_date",
    "merchant_paid_date",
    "deferred_date",
  ],

  // Plain numeric fields — searched as-is
  numberFields: ["bp_price", "funding_factor", "hdw_payment_split_term"],

  // Money (cents) fields — use _usd predicates so user enters dollars
  moneyFields: [
    "per_transaction_cents",
    "projected_volume_cents",
    "actual_volume_cents",
    "invoices_invoice_amount_cents",
    "invoices_balance_due_cents",
    "monthly_payment_cents",
    "hardware_cost_cents",
    "overhead_cents",
    "marketing_deployment_cost_cents",
    "agent_price_cents",
    "agent_split_cents",
    "contribution_margin_cents",
    "company_net_cents",
    "agent_deduction_cents",
    "ae_mid_commission_cents",
    "sm_commission_cents",
    "gsm_commission_cents",
  ],

  trueFalseFields: ["pci_required", "iris_connection"],

  // Maps field name → association option source
  associationFields: {
    merchant_name: {
      url: "/merchants.json",
      searchParam: "q[name_or_mid_or_iris_crm_lead_id_text_cont]",
      valueField: "name",
      labelField: "name",
    },
    platform_name: { url: "/platforms.json" },
    processor_name: { url: "/processors.json" },
    sales_channel_name: { url: "/sales_channels.json" },
    deployment_status_name: { url: "/deployment_statuses.json" },
  },

  // Maps field name → array of [value, label] pairs
  enumFields: {
    application_status: [
      [0, "Approved"],
      [1, "Declined"],
      [2, "In Process"],
      [3, "Withdraw"],
    ],
    mid_type: [
      [0, "New Board"],
      [1, "Re Board"],
      [2, "Existing"],
    ],
    cc_ach_form: [
      [0, "ACH Authorization"],
      [1, "Credit Card Authorization"],
      [2, "Biller Genie"],
      [3, "Subscription"],
      [4, "Cash Sale-Cash"],
      [5, "Cash Sale-Check"],
      [6, "Cash Sale-Credit Card"],
      [7, "Not Applicable"],
      [8, "Cash Sale-ACH"],
      [9, "Cash Sale-Payment Link"],
      [10, "Free"],
    ],
    hardware_payment_term: [
      [0, "Invoice Merchant"],
      [1, "Agent Pay Split"],
      [2, "Agent Pay"],
      [3, "Free"],
      [4, "Not Applicable"],
    ],
    menu_status: [
      [0, "No Menu"],
      [1, "Under Process"],
      [2, "Pending by Customer"],
      [3, "Deployed"],
    ],
    risk_status: [
      [0, "High Risk"],
      [1, "Low Risk"],
      [2, "TBD"],
    ],
    leasing_status: [
      [0, "Approved"],
      [1, "Declined"],
    ],
    leasing_grade: [
      [0, "A"],
      [1, "B"],
      [2, "C"],
      [3, "D"],
      [4, "E"],
    ],
    leasing_term: [
      [12, "12"],
      [24, "24"],
      [36, "36"],
      [48, "48"],
    ],
    subscription_status: [
      [0, "App Submitted"],
      [1, "Graded"],
      [2, "D&A Submitted"],
      [3, "Funded"],
      [4, "Agent Paid"],
      [5, "SM Paid"],
      [6, "DSM Paid"],
      [7, "Signup Bonus Due"],
      [8, "Signup Bonus Payout"],
      [9, "Deferred Subscription"],
    ],
  },
};

// Connects to data-controller="search--condition"
export default class extends Controller {
  static targets = ["attributeSelect", "predicateSelect", "valueInput"];

  async connect() {
    this.clonedValueInput = this.valueInputTarget.cloneNode(true);

    const fieldValue = this.attributeSelectTarget?.value;
    const predicateValue = this.predicateSelectTarget?.value;

    if (fieldValue) {
      await this._addPredicateOptions(fieldValue);

      if (predicateValue) {
        this.predicateSelectTarget.value = predicateValue;
        this.predicateChanged({ target: { value: predicateValue } });
      }
    }
  }

  async attributeChanged(event) {
    const value = event.target.value;

    // Clear the predicate select options
    this.predicateSelectTarget.innerHTML = "";

    // Add the proper predicates
    await this._addPredicateOptions(value);

    // Show the predicate select field
    this.predicateSelectTarget
      .closest(".predicate-select-container")
      ?.classList.remove("d-none");
  }

  predicateChanged(event) {
    const value = event.target.value;
    if (value) {
      this.valueInputTarget.closest(".value-col").classList.remove("d-none");
    }
  }

  async _addPredicateOptions(value) {
    if (FIELD_CONFIG.dateFields.includes(value)) {
      this._createDatePredicate();
      this._changeInputType("date");
    } else if (FIELD_CONFIG.moneyFields.includes(value)) {
      this._createMoneyPredicate();
      this._changeInputType("text");
    } else if (FIELD_CONFIG.numberFields.includes(value)) {
      this._createNumberPredicate();
      this._changeInputType("text");
    } else if (FIELD_CONFIG.trueFalseFields.includes(value)) {
      this._createTrueFalsePredicate();
      this._changeInputType("select", [
        [true, "Yes"],
        [false, "No"],
      ]);
    } else if (value in FIELD_CONFIG.associationFields) {
      this._createAssociationPredicate();
      await this._createAssociationInput(value);
    } else if (value in FIELD_CONFIG.enumFields) {
      this._createEnumPredicate();
      this._changeInputType("select", FIELD_CONFIG.enumFields[value]);
    } else {
      this._createDefaultPredicate();
      this._changeInputType("text");
    }
  }

  _createDatePredicate() {
    const datePredicates = [
      ["lt", "Before"],
      ["lteq", "On or Before"],
      ["gt", "After"],
      ["gteq", "On or After"],
      ["null", "Is Empty"],
      ["not_null", "Is Not Empty"],
    ];

    this._createPredicateOptions(datePredicates);
  }

  _createMoneyPredicate() {
    const moneyPredicates = [
      ["eq_usd", "Equals ($)"],
      ["not_eq_usd", "Does Not Equal ($)"],
      ["lt_usd", "Less Than ($)"],
      ["lteq_usd", "Less Than or Equal To ($)"],
      ["gt_usd", "Greater Than ($)"],
      ["gteq_usd", "Greater Than or Equal To ($)"],
      ["null", "Is Empty"],
      ["not_null", "Is Not Empty"],
    ];

    this._createPredicateOptions(moneyPredicates);
  }

  _createNumberPredicate() {
    const numberPredicates = [
      ["eq", "Equals"],
      ["not_eq", "Does Not Equal"],
      ["lt", "Less Than"],
      ["lteq", "Less Than or Equal To"],
      ["gt", "Greater Than"],
      ["gteq", "Greater Than or Equal To"],
      ["null", "Is Empty"],
      ["not_null", "Is Not Empty"],
    ];

    this._createPredicateOptions(numberPredicates);
  }

  _createEnumPredicate() {
    const enumPredicates = [
      ["eq", "Equals"],
      ["not_eq", "Does Not Equal"],
      ["blank", "Is Blank"],
      ["null", "Is Empty"],
      ["not_null", "Is Not Empty"],
    ];

    this._createPredicateOptions(enumPredicates);
  }

  _createDefaultPredicate() {
    const defaultPredicates = [
      ["cont", "Contains"],
      ["eq", "Equals"],
      ["not_eq", "Does Not Equal"],
      ["start", "Starts With"],
      ["not_start", "Does Not Start With"],
      ["end", "Ends With"],
      ["not_end", "Does Not End With"],
      ["blank", "Is Blank"],
      ["null", "Is Empty"],
      ["not_null", "Is Not Empty"],
    ];

    this._createPredicateOptions(defaultPredicates);
  }

  _createTrueFalsePredicate() {
    const trueFalsePredicates = [
      ["eq", "Equals"],
      ["not_eq", "Does Not Equal"],
      ["blank", "Is Blank"],
      ["null", "Is Empty"],
      ["not_null", "Is Not Empty"],
    ];

    this._createPredicateOptions(trueFalsePredicates);
  }

  _createAssociationPredicate() {
    const associationPredicate = [
      ["eq", "Equals"],
      ["not_eq", "Does Not Equal"],
      ["blank", "Is Blank"],
      ["null", "Is Empty"],
      ["not_null", "Is Not Empty"],
    ];

    this._createPredicateOptions(associationPredicate);
  }

  _createPredicateOptions(predicates) {
    this.predicateSelectTarget.innerHTML = "";

    const blankOpt = document.createElement("option");
    blankOpt.innerText = "";
    blankOpt.value = "";
    this.predicateSelectTarget.appendChild(blankOpt);

    for (const predicate of predicates) {
      const opt = document.createElement("option");
      opt.innerText = predicate[1];
      opt.value = predicate[0];

      this.predicateSelectTarget.appendChild(opt);
    }
  }

  _changeInputType(type, selectOptions = []) {
    if (type === "date" || type === "text") {
      this.valueInputTarget.replaceWith(this.clonedValueInput);
      this.valueInputTarget.type = type;
    }

    if (type === "select") {
      const inputName = this.valueInputTarget.name;
      const selectElement = document.createElement("select");

      selectElement.name = inputName;
      selectElement.classList.add("form-select");
      selectElement.setAttribute("data-search--condition-target", "valueInput");

      const blankOption = document.createElement("option");
      selectElement.appendChild(blankOption);

      for (const selectOption of selectOptions) {
        const option = document.createElement("option");
        option.value = selectOption[0];
        option.textContent = selectOption[1];

        selectElement.appendChild(option);
      }

      selectElement.value = this.clonedValueInput.value;
      this.valueInputTarget.replaceWith(selectElement);
    }
  }

  async _createAssociationInput(field) {
    const inputName = this.valueInputTarget.name;
    const selectElement = document.createElement("select");

    selectElement.name = inputName;
    selectElement.classList.add("form-select");
    selectElement.setAttribute("data-search--condition-target", "valueInput");
    selectElement.setAttribute("data-controller", "tom-select");

    const blankOption = document.createElement("option");
    selectElement.appendChild(blankOption);

    const associationConfig = FIELD_CONFIG.associationFields[field];
    if (associationConfig.searchParam) {
      selectElement.setAttribute(
        "data-tom-select-url-value",
        associationConfig.url,
      );
      selectElement.setAttribute(
        "data-tom-select-search-param-value",
        associationConfig.searchParam,
      );
      selectElement.setAttribute(
        "data-tom-select-value-field-value",
        associationConfig.valueField,
      );
      selectElement.setAttribute(
        "data-tom-select-label-field-value",
        associationConfig.labelField,
      );

      if (this.clonedValueInput.value) {
        const option = document.createElement("option");
        option.value = this.clonedValueInput.value;
        option.textContent = this.clonedValueInput.value;
        option.selected = true;
        selectElement.appendChild(option);
      }

      this.valueInputTarget.replaceWith(selectElement);
      return;
    }

    const response = await get(associationConfig.url);
    if (response.ok) {
      const body = await response.json;

      for (const obj of body) {
        const option = document.createElement("option");
        option.value = obj.name;
        option.textContent = obj.name;

        selectElement.appendChild(option);
      }
    }

    selectElement.value = this.clonedValueInput.value;
    this.valueInputTarget.replaceWith(selectElement);
  }
}
